import Close from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import {
  Card,
  CardContent,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import { GreenText, OrderableTableCell } from 'components'
import { moveToInvoicingServices } from 'controllers/invoicing/index'
import { InvoicingDto, VehicleEligibleForInvoicingDto } from 'dtos/invoicing'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { clientSideOrderBy, clientSidePagination } from 'utils'

interface VehiclesEligibleForInvoicingCardProps {
  refreshInvoicing: (invoicing: InvoicingDto) => void
  vehiclesEligibleForInvoicing: VehicleEligibleForInvoicingDto[]
}

const vehiclesEligibleSelectionCellSx = {
  boxSizing: 'border-box',
  maxWidth: 40,
  minWidth: 40,
  padding: '2px 4px !important',
  verticalAlign: 'middle',
  width: 40
}

export default function VehiclesEligibleForInvoicingCard({ refreshInvoicing, vehiclesEligibleForInvoicing }: VehiclesEligibleForInvoicingCardProps) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof VehicleEligibleForInvoicingDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [selected, setSelected] = useState<VehicleEligibleForInvoicingDto[]>([])

  const orderedVehicles = [...vehiclesEligibleForInvoicing].sort(clientSideOrderBy<VehicleEligibleForInvoicingDto>(orderBy, order))
  const visibleVehicles = orderedVehicles.slice(...clientSidePagination(page, pageSize))
  const selectedVisibleCount = visibleVehicles.filter(vehicle => selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)).length
  const allVisibleSelected = visibleVehicles.length > 0 && selectedVisibleCount === visibleVehicles.length
  const selectAllTooltip = `${allVisibleSelected ? 'Deselect' : 'Select'} all vehicles on this page (${visibleVehicles.length})`

  const onChangeSortByDirection = (propertyName: keyof VehicleEligibleForInvoicingDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  useEffect(() => {
    setPage(0)
    setSelected([])
  }, [pageSize])

  useEffect(() => {
    setSelected(currentSelected =>
      currentSelected
        .filter(selectedVehicle => vehiclesEligibleForInvoicing.some(vehicle => vehicle.vehicleGuid === selectedVehicle.vehicleGuid))
        .map(selectedVehicle => {
          const currentVehicle = vehiclesEligibleForInvoicing.find(vehicle => vehicle.vehicleGuid === selectedVehicle.vehicleGuid)
          return currentVehicle ? { ...currentVehicle, ...selectedVehicle } : selectedVehicle
        })
    )
  }, [vehiclesEligibleForInvoicing])

  const toggleSelectAll = () => {
    setSelected(currentSelected => {
      if (allVisibleSelected) {
        return currentSelected.filter(selectedVehicle => !visibleVehicles.some(vehicle => vehicle.vehicleGuid === selectedVehicle.vehicleGuid))
      }

      const selectedVehicleGuids = new Set(currentSelected.map(vehicle => vehicle.vehicleGuid))
      return [...currentSelected, ...visibleVehicles.filter(vehicle => !selectedVehicleGuids.has(vehicle.vehicleGuid))]
    })
  }

  const selectAllCheckbox = (
    <Checkbox
      checked={allVisibleSelected}
      disabled={isSubmitting || visibleVehicles.length === 0}
      id='vehicles-eligible-select-all'
      indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
      inputProps={{ 'aria-label': selectAllTooltip }}
      onChange={toggleSelectAll}
      size='small'
    />
  )

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12} container alignItems='center' spacing={1}>
            <Grid item>
              <Typography fontWeight='bold' variant='h6'>
                Vehicles Eligible For Invoicing
              </Typography>
            </Grid>
            {selected.length > 0 && (
              <Grid item>
                <Chip
                  deleteIcon={
                    <Tooltip title='Clear all selections'>
                      <span>
                        <IconButton aria-label='Clear all selections' disabled={isSubmitting} id='vehicles-eligible-clear-selection' size='small'>
                          <Close fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                  label={`${selected.length} selected`}
                  onDelete={() => setSelected([])}
                />
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table
                size='small'
                sx={theme => ({
                  tableLayout: 'auto',
                  '& .MuiTableCell-head': {
                    color: theme.palette.primary.main,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    lineHeight: 1.25,
                    py: 0.75,
                    px: 0.75,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    verticalAlign: 'bottom'
                  },
                  '& .MuiTableCell-body': {
                    fontSize: '9pt',
                    lineHeight: 1.35,
                    py: 0.5,
                    px: 0.75,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    verticalAlign: 'top'
                  },
                  '& .MuiTableCell-paddingCheckbox': {
                    py: 0.25,
                    px: 0.5,
                    width: 40,
                    verticalAlign: 'middle'
                  },
                  '& .MuiTablePagination-root': {
                    fontSize: '9pt'
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
                    fontSize: '9pt'
                  },
                  '& .MuiTablePagination-toolbar': {
                    minHeight: 40
                  }
                })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      align='left'
                      padding='checkbox'
                      sx={vehiclesEligibleSelectionCellSx}
                    >
                      {isSubmitting || visibleVehicles.length === 0 ? selectAllCheckbox : <Tooltip title={selectAllTooltip}>{selectAllCheckbox}</Tooltip>}
                    </TableCell>

                    <OrderableTableCell<VehicleEligibleForInvoicingDto>
                      keyName='importerStockNumber'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Importer Stock #
                    </OrderableTableCell>

                    <OrderableTableCell<VehicleEligibleForInvoicingDto>
                      keyName='buyerExporterCompany'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Buyer/Exporter
                    </OrderableTableCell>

                    <OrderableTableCell<VehicleEligibleForInvoicingDto>
                      keyName='papsNumber'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      PAPS #
                    </OrderableTableCell>

                    <OrderableTableCell<VehicleEligibleForInvoicingDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      VIN
                    </OrderableTableCell>

                    <OrderableTableCell<VehicleEligibleForInvoicingDto> keyName='year' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Year
                    </OrderableTableCell>

                    <OrderableTableCell<VehicleEligibleForInvoicingDto> keyName='make' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Make
                    </OrderableTableCell>

                    <OrderableTableCell<VehicleEligibleForInvoicingDto> keyName='model' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Model
                    </OrderableTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleVehicles.map((vehicle, index) => (
                    <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                        <TableCell padding='checkbox' sx={vehiclesEligibleSelectionCellSx}>
                        <Checkbox
                          checked={selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)}
                          disabled={isSubmitting}
                          id={`vehicles-eligible-select-${vehicle.vehicleGuid}`}
                          inputProps={{ 'aria-label': `Select vehicle ${vehicle.vin}` }}
                          onChange={() => {
                            setSelected(currentSelected =>
                              currentSelected.some(value => value.vehicleGuid === vehicle.vehicleGuid)
                                ? currentSelected.filter(value => value.vehicleGuid !== vehicle.vehicleGuid)
                                : [...currentSelected, vehicle]
                            )
                          }}
                          size='small'
                        />
                      </TableCell>

                      <TableCell>{vehicle.importerStockNumber}</TableCell>

                      <TableCell>{vehicle.buyerExporterCompany}</TableCell>

                      <TableCell>{vehicle.papsNumber}</TableCell>

                      <TableCell>{vehicle.vin}</TableCell>

                      <TableCell>{vehicle.year}</TableCell>

                      <TableCell>{vehicle.make}</TableCell>

                      <TableCell>{vehicle.model}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={vehiclesEligibleForInvoicing.length ?? 0}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Invoicing Process:**

Within the Invoicing feature, begin by selecting the vehicle or vehicles you want to process from the Vehicles Eligible for Invoicing table. Then click Move Selected Vehicles to Invoicing Services.

Because BWI should only pass an outbound transportation charge to the customer when BWI is responsible for that transportation, VTS2 will validate each selected vehicle when it is moved from Vehicles Eligible for Invoicing to Invoicing Services.

For each selected VIN, VTS2 will determine whether the vehicle is associated with an outbound trip. If an outbound trip exists, VTS2 will then verify whether the Outbound Transporter Company is Blue Water Importers or Great Lakes Car Carrier.

If either of the following conditions is true, VTS2 will clear the Outbound Transportation value to zero and disable the field:
   - The vehicle is not associated with an outbound trip.
   - The vehicle is associated with an outbound trip, but the Outbound Transporter Company is neither Blue Water Importers nor Great Lakes Car Carrier.

When the field is disabled, users will not be able to manually enter an Outbound Transportation value or populate the field using the existing Each/Split functionality.

In the Invoicing Services section, review the dollar amount shown for each service column for every selected vehicle. Verify that each amount is correct and make any necessary adjustments.

Once the service amounts have been confirmed, select the applicable vehicles in the Invoicing Services table and click Save and Mark Selected Vehicles as Ready to Invoice. The selected vehicles will then move to the Ready for Invoicing table.

From the Ready for Invoicing table, select the vehicles you want to send to QuickBooks and click Save and Send Selected Vehicles to QuickBooks.

Next, on the computer where QuickBooks is installed, run the QuickBooks Sync application. The sync process sends the required data from VTS2 to QuickBooks. After the invoices are created, the sync application retrieves the invoice numbers from QuickBooks and sends them back to VTS2. The invoice numbers will then appear in the Invoice Number column for the applicable vehicles in the Ready for Invoicing table.

After invoicing is complete, select the applicable vehicles and click Save and Mark Selected Vehicles as Invoiced. The vehicles will move to the Invoiced table, where they are retained for historical record keeping.

**Vehicles Eligible For Invoicing:**

This list displays vehicles where the "Sent To DOT" field under Vehicle Details → Conformity (tab) on the Conformity Details card contains a date.

**Move Selected Vehicles To Invoicing Services:**

Moves selected vehicles from the Vehicles Eligible For Invoicing list to the Invoicing Services list.`}
            </GreenText>
          </Grid>

          <Grid item container alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <LoadingButton
                disabled={selected.length === 0}
                loading={isSubmitting}
                onClick={() => {
                  setIsSubmitting(true)
                  moveToInvoicingServices(selected)
                    .then(res => {
                      enqueueSnackbar('Vehicles Moved To Invoicing Services Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSubmitting(false))
                }}
                size='small'
                variant='contained'
              >
                MOVE SELECTED VEHICLES TO INVOICING SERVICES
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
